/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */

import { useEffect, useState } from 'react';
import { CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface SubscriptionSuccessProps {
  onContinue: () => void;
}

export const SubscriptionSuccess = ({ onContinue }: SubscriptionSuccessProps) => {
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onContinue();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onContinue]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
      <Card className="max-w-md w-full text-center">
        <CardHeader>
          <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Welcome to Pro!</CardTitle>
          <CardDescription>
            Your subscription has been activated successfully
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="flex items-center justify-center gap-2 text-indigo-700 font-medium mb-2">
              <Sparkles className="h-5 w-5" />
              You now have access to:
            </div>
            <ul className="text-sm text-indigo-600 space-y-1">
              <li>All keys and scales</li>
              <li>Unlimited Harmonic Companion</li>
              <li>Unlimited Jam Tracks</li>
              <li>Advanced practice modes</li>
            </ul>
          </div>

          <div className="space-y-2">
            <Button onClick={onContinue} className="w-full">
              Start Exploring
            </Button>
            <p className="text-xs text-muted-foreground">
              Redirecting in {countdown} seconds...
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
